import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Brain,
  FileText,
  PencilLine,
  ShieldCheck,
  Sparkles,
  Timer,
  type LucideIcon,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { TargetSpot } from "@/platform/ui/illustrations/spot";

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";

interface Step {
  icon: LucideIcon;
  title: string;
  body: string;
  duration: string;
  tone: Tone;
}

const steps: Step[] = [
  {
    icon: Brain,
    title: "25 objetivas equilibradas",
    body: "Distribuídas por Português, Matemática, Ciências da Natureza, Inglês e Humanas. Tempo médio de 1m30s por questão.",
    duration: "≈ 38 min",
    tone: "violet",
  },
  {
    icon: PencilLine,
    title: "1 redação curta",
    body: "Tema dissertativo-argumentativo no padrão Vunesp. Avaliada pela rubrica oficial em 4 critérios.",
    duration: "≈ 35 min",
    tone: "pink",
  },
  {
    icon: FileText,
    title: "2 discursivas",
    body: "Uma de ciências, uma de matemática — com subitens e exigência de justificativa.",
    duration: "≈ 25 min",
    tone: "mint",
  },
  {
    icon: Timer,
    title: "Leitura cronometrada",
    body: "Texto-base do mesmo gênero que abre a prova Einstein, com 5 questões de interpretação.",
    duration: "≈ 22 min",
    tone: "yellow",
  },
];

const TONE_BG: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

const TONE_BADGE: Record<Tone, "accent" | "pink" | "mint" | "yellow" | "coral" | "sky"> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
  coral: "coral",
  sky: "sky",
};

export function DiagnosisPage() {
  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 top-80 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-14 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-20 md:py-28">
        {/* HERO */}
        <header className="animate-fade-up grid items-end gap-10 md:grid-cols-[1.4fr_1fr]">
          <div>
            <Badge variant="accentSolid" className="w-fit">
              <Sparkles className="size-3" aria-hidden />
              Diagnóstico
            </Badge>
            <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
              Em duas horas, uma{" "}
              <span className="text-gradient-accent">foto honesta</span> de onde você está.
            </h1>
            <p className="mt-5 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              O diagnóstico simula uma seção reduzida da prova Einstein. Não é avaliação para
              passar ou reprovar — é uma régua para calibrar seu plano.
            </p>
          </div>
          <div className="flex justify-center md:justify-end">
            <TargetSpot className="w-56 animate-float" />
          </div>
        </header>

        {/* STEPS */}
        <div className="mt-16 grid gap-5 md:grid-cols-2">
          {steps.map(({ icon: Icon, title, body, duration, tone }, i) => (
            <article
              key={title}
              className="lift relative overflow-hidden rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 md:p-8 shadow-[var(--shadow-sm)] animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start justify-between">
                <span className={`chip size-12 ${TONE_BG[tone]}`}>
                  <Icon className="size-5" aria-hidden />
                </span>
                <Badge variant={TONE_BADGE[tone]}>{duration}</Badge>
              </div>
              <h2 className="mt-6 font-display text-[length:var(--text-h2)] font-semibold leading-[var(--text-h2--line-height)] tracking-[var(--text-h2--letter-spacing)]">
                {title}
              </h2>
              <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] text-pretty">
                {body}
              </p>
            </article>
          ))}
        </div>

        {/* RESULTADO */}
        <div className="mt-16 relative overflow-hidden rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-8 md:p-12 shadow-[var(--shadow-md)]">
          <Blob
            color="violet"
            className="pointer-events-none absolute -right-20 -top-20 w-72 opacity-25 blur-2xl"
          />
          <div className="relative">
            <Badge variant="accent">O que você recebe</Badge>
            <h3 className="mt-3 font-display text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)] text-balance">
              Relatório em menos de 24 horas.
            </h3>
            <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              Conta o que move a nota agora e o que move depois.
            </p>

            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[
                {
                  kicker: "Foto inicial",
                  text: "Vetor de proficiência calibrado por Elo em 10 habilidades nomeadas.",
                  tone: "violet" as Tone,
                },
                {
                  kicker: "Nota projetada",
                  text: "Nota bruta + padronizada estimada com intervalo de confiança.",
                  tone: "pink" as Tone,
                },
                {
                  kicker: "Três focos",
                  text: "As três áreas com maior ganho marginal estimado e plano semanal.",
                  tone: "mint" as Tone,
                },
              ].map((b) => (
                <div key={b.kicker} className="rounded-lg bg-[color:var(--color-paper)] p-5">
                  <Badge variant={TONE_BADGE[b.tone]}>{b.kicker}</Badge>
                  <p className="mt-3 text-[length:var(--text-body-sm)] text-pretty">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA + footer */}
        <div className="mt-16 flex flex-col items-start gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-4 py-2 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)] shadow-[var(--shadow-xs)]">
            <ShieldCheck className="size-3.5 text-[color:var(--color-mint-deep)]" aria-hidden />
            Seu resultado só você vê.
          </div>
          <Button asChild size="xl" variant="gradient">
            <Link to="/diagnostico/sessao">
              Começar diagnóstico agora
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          </Button>
        </div>
      </Container>

      {/* hidden import keeper */}
      <BookOpen className="hidden" />
    </div>
  );
}
